import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";
import ProductGrid from "./ProductGrid";
import CardPanel from "./CardPanel";
import PartnerApi from "../../helpers/PartnerApi";
import i18n from "../../i18n";

const LICENSE_NO = "BABDEMOMPVWV";
const DEFAULT_LANGUAGE = "tr";
const ALLOWED_LANGUAGES = ["tr", "en", "de", "fr", "nl"];

const MainLayout = () => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [calculatedCart, setCalculatedCart] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [token, setToken] = useState("");

  const [confirmError, setConfirmError] = useState("");
  const [pageError, setPageError] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const [isAuthorizedEntry, setIsAuthorizedEntry] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);

      const rawLanguage = (params.get("language") || DEFAULT_LANGUAGE)
        .trim()
        .toLowerCase();

      const tokenParam = (params.get("token") || "").trim();

      const finalLanguage = ALLOWED_LANGUAGES.includes(rawLanguage)
        ? rawLanguage
        : DEFAULT_LANGUAGE;

      setLanguage(finalLanguage);
      setToken(tokenParam);
      i18n.changeLanguage(finalLanguage);

      const hasRequiredParams = tokenParam.length > 0;
      setIsAuthorizedEntry(hasRequiredParams);

      if (!hasRequiredParams) {
        setPageError("Bu sayfa AKEAD'dan açılmalıdır.");
      } else {
        setPageError("");
      }
    } catch (error) {
      console.error("Param okuma hatası:", error);
      setIsAuthorizedEntry(false);
      setPageError("Bağlantı hatalı.");
    }
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!isAuthorizedEntry || !token) {
        setProducts([]);
        return;
      }

      try {
        setProductsLoading(true);
        setPageError("");

        const res = await PartnerApi.getItemList({
          language,
          token,
        });

        const mapped = (res?.itemList || []).map((item, index) => ({
          id: item.code || index + 1,
          code: item.code,
          name: item.description || "",
          image: item.imageUrl || "",
          unitPrice: Number(item.price || 0),
          currency: item.currency || "EUR",
        }));

        setProducts(mapped);
      } catch (err) {
        console.error(err);

        if (err?.code === 1013) {
          setPageError("Oturumun süresi doldu.");
        } else if (err?.code === 1012) {
          setPageError("Geçersiz oturum.");
        } else {
          setPageError(err?.message || "Ürün listesi alınamadı.");
        }

        setIsAuthorizedEntry(false);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, [token, language, isAuthorizedEntry]);

  useEffect(() => {
    const runCalculateCart = async () => {
      if (!isAuthorizedEntry || !token || cart.length === 0) {
        setCalculatedCart(null);
        return;
      }

      try {
        const res = await PartnerApi.calculateCart({
          licenseNo: LICENSE_NO,
          language,
          token,
          itemList: cart.map((item) => ({
            code: item.code,
            quantity: Number(item.quantity || 0),
          })),
        });

        setCalculatedCart(res?.calculateCart || null);
      } catch (err) {
        console.error(err);
        setCalculatedCart(null);
      }
    };

    runCalculateCart();
  }, [cart, token, language, isAuthorizedEntry]);

  const handleChangeQuantity = (product, type) => {
    if (!isAuthorizedEntry) return;

    setCart((prev) => {
      const existing = prev.find((x) => x.code === product.code);

      if (type === "increase") {
        if (existing) {
          return prev.map((x) =>
            x.code === product.code
              ? { ...x, quantity: x.quantity + 1 }
              : x
          );
        }
        return [...prev, { ...product, quantity: 1 }];
      }

      if (type === "decrease") {
        if (!existing) return prev;

        return prev
          .map((x) =>
            x.code === product.code
              ? { ...x, quantity: x.quantity - 1 }
              : x
          )
          .filter((x) => x.quantity > 0);
      }

      return prev;
    });
  };

  const mergedCart = useMemo(() => {
    const apiItems = calculatedCart?.itemList || [];

    return cart.map((cartItem) => {
      const matched = apiItems.find((x) => x.code === cartItem.code);

      return {
        ...cartItem,
        quantity: cartItem.quantity,
        unitPrice: Number(matched?.price ?? cartItem.unitPrice ?? 0),
        image: matched?.imageUrl ?? cartItem.image,
        name: matched?.description ?? cartItem.name,
      };
    });
  }, [cart, calculatedCart]);

  const cartCount = mergedCart.length;

  const totalAmount = useMemo(() => {
    return mergedCart.reduce(
      (sum, item) => sum + item.unitPrice * item.quantity,
      0
    );
  }, [mergedCart]);

  const cartSummary = useMemo(() => {
    const source = calculatedCart || {};

    const totalWithoutTax = Number(source.totalWithoutTax || 0);
    const totalPayable = Number(source.totalPayable || totalAmount || 0);
    const totalTax = totalPayable - totalWithoutTax;

    return {
      totalExclTax: totalWithoutTax,
      totalTax: totalTax > 0 ? totalTax : 0,
      totalToPay: totalPayable,
    };
  }, [calculatedCart, totalAmount]);

  const handleConfirmCart = async () => {
    if (!isAuthorizedEntry || !token) return;
    if (!cart.length || confirmLoading) return;

    try {
      setConfirmLoading(true);
      setConfirmError("");

      const res = await PartnerApi.confirmCart({
        licenseNo: LICENSE_NO,
        language,
        token,
        itemList: cart.map((item) => ({
          code: item.code,
          quantity: item.quantity,
        })),
      });

      const paymentUrl = res?.payment?.url || "";

      if (paymentUrl) {
        window.location.assign(paymentUrl);
      } else {
        setConfirmError("Ödeme linki alınamadı.");
      }
    } catch (err) {
      console.error(err);
      setConfirmError(
        err?.message || "Sepet onayı sırasında bir hata oluştu."
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Header
        cartCount={cartCount}
        onCartClick={() => setMobileCartOpen(true)}
        language={language}
      />

      <main className="mx-auto max-w-[1800px] px-3 py-3 sm:px-4 sm:py-4 lg:px-5">
        {pageError && (
          <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700">
            {pageError}
          </div>
        )}

        {isAuthorizedEntry && (
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:gap-5">
            <div className="min-w-0 flex-1">
              {productsLoading ? (
                <div className="rounded-xl bg-white p-6 text-center text-slate-600 shadow-sm">
                  Yükleniyor...
                </div>
              ) : (
                <ProductGrid
                  products={products}
                  cart={mergedCart}
                  onChangeQuantity={handleChangeQuantity}
                  language={language}
                />
              )}
            </div>

            <div className="hidden xl:block xl:w-[340px] xl:shrink-0">
              <div className="sticky top-[88px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <CardPanel
                  cart={mergedCart}
                  totalAmount={totalAmount}
                  cartSummary={cartSummary}
                  language={language}
                  onChangeQuantity={handleChangeQuantity}
                  onConfirmCart={handleConfirmCart}
                  confirmLoading={confirmLoading}
                  confirmError={confirmError}
                />
              </div>
            </div>
          </div>
        )}
      </main>

      {mobileCartOpen && (
        <div className="fixed inset-0 z-50 xl:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileCartOpen(false)}
          />

          <div className="absolute bottom-0 left-0 right-0 h-[82vh] overflow-hidden rounded-t-[28px] bg-white shadow-2xl">
            <CardPanel
              cart={mergedCart}
              totalAmount={totalAmount}
              cartSummary={cartSummary}
              language={language}
              onChangeQuantity={handleChangeQuantity}
              mobile
              onClose={() => setMobileCartOpen(false)}
              onConfirmCart={handleConfirmCart}
              confirmLoading={confirmLoading}
              confirmError={confirmError}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MainLayout;